import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BarChart3, ChevronLeft, ChevronRight, Brain, Loader2 } from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";
import { DiDiagnostics } from "../../components/admin/di/DiDiagnostics";
import { DiFilters } from "../../components/admin/di/DiFilters";
import { DiRescorePanel } from "../../components/admin/di/DiRescorePanel";
import { DiTable } from "../../components/admin/di/DiTable";
import { TIER_ORDER, TIER_STYLE, titleCase } from "../../components/admin/di/diFormat";

const DEFAULT_FILTERS = { page: 1, page_size: 25, sort: "deal_score", order: "desc", out_of_scope: false };

export default function DealIntelligence() {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, tier_counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagError, setDiagError] = useState("");
  const [activeDiag, setActiveDiag] = useState("total");
  const [expanded, setExpanded] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const setF = useCallback((patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 })), []);
  const reset = useCallback(() => { setFilters(DEFAULT_FILTERS); setActiveDiag("total"); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));
      const r = await axios.get(`${API}/deal-intelligence`, { headers, params });
      setData(r.data); setError("");
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [filters, headers]);

  const loadDiag = useCallback(async () => {
    try { const r = await axios.get(`${API}/deal-intelligence/diagnostics`, { headers }); setDiag(r.data); setDiagError(""); }
    catch (e) { setDiagError(e?.response?.data?.detail || e.message); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDiag(); }, [loadDiag]);

  const refreshAll = useCallback(() => { load(); loadDiag(); }, [load, loadDiag]);

  const pickDiag = (metricFilter, key) => {
    setActiveDiag(key);
    // Diagnostics count across ALL deals, so metric drill-downs include out-of-scope records
    setFilters({ ...DEFAULT_FILTERS, out_of_scope: key === "total" ? false : "all", ...metricFilter });
  };

  const aiScoreAll = async () => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/deals/ai-score-all?only_unscored=true&limit=10`, {}, { headers });
      toast({ title: `Queued ${r.data.queued} deal(s) for AI scoring`, description: "Runs in the background — refresh in a minute." });
    } catch (e) { toast({ title: "AI scoring failed", description: e?.response?.data?.detail || e.message, variant: "destructive" }); }
    finally { setAiBusy(false); }
  };

  const tierTotal = Object.values(data.tier_counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="deal-intelligence-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2342] font-['Playfair_Display'] flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#C8A96B]" /> Deal Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">One authoritative Deal Score (0–100) · AI Score kept separate · tiers computed server-side</p>
        </div>
        <button type="button" onClick={aiScoreAll} disabled={aiBusy} data-testid="di-ai-score-all-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-300 bg-white text-sm font-semibold text-[#0A2342] disabled:opacity-50">
          {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />} AI Score Unscored (10)
        </button>
      </header>

      <DiDiagnostics diagnostics={diag} loading={!diag} error={diagError} active={activeDiag}
        onPick={(f) => pickDiag(f, Object.keys(f).length === 0 ? "total" : (f.missing ? `missing_${f.missing}` : f.duplicates ? "duplicate_candidates" : f.out_of_scope ? "out_of_scope" : f.restricted ? "restricted_housing" : f.ai_missing ? "ai_score_missing" : "score_disagreements"))} />

      <DiRescorePanel headers={headers} onCompleted={refreshAll} />

      <div className="flex flex-wrap gap-2" data-testid="di-tier-summary">
        {TIER_ORDER.map((t) => (
          <button key={t} type="button" onClick={() => setF({ tier: filters.tier === t ? undefined : t })} data-testid={`di-tier-chip-${t}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${filters.tier === t ? "ring-2 ring-[#C8A96B]" : ""} ${TIER_STYLE[t]} border-transparent`}>
            {titleCase(t)} · {data.tier_counts?.[t] ?? 0}
          </button>
        ))}
        <span className="self-center text-xs text-slate-500">{tierTotal} in current filter{data.total !== tierTotal ? ` (${data.total} total)` : ""}</span>
      </div>

      <DiFilters f={filters} set={setF} reset={reset} count={data.total} />

      <DiTable items={data.items} loading={loading} error={error} expanded={expanded} onToggle={(id) => setExpanded((e) => (e === id ? null : id))}
        headers={headers} onChanged={refreshAll} toast={toast} />

      <nav className="flex items-center justify-between text-sm text-slate-600" data-testid="di-pagination">
        <span>Page {data.page || filters.page} of {data.pages} · {data.total} deals</span>
        <div className="flex items-center gap-2">
          <select data-testid="di-page-size" className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm" value={filters.page_size} onChange={(e) => setF({ page_size: Number(e.target.value) })}>
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button type="button" disabled={filters.page <= 1} onClick={() => setF({ page: filters.page - 1 })} data-testid="di-prev-page" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" disabled={filters.page >= data.pages} onClick={() => setF({ page: filters.page + 1 })} data-testid="di-next-page" className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </nav>
    </div>
  );
}
